import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import API from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Pill, ArrowRight, Loader2, Info } from "lucide-react";
import PublicFooter from "@/components/PublicFooter";
import MedicationInfoModal from "@/components/MedicationInfoModal";
import VialIllustration from "@/components/VialIllustration";

/**
 * Map the medication's base name → the vial illustration variant.
 * Falls back to "semaglutide" (violet) so a newly-added drug still gets a
 * presentable mock vial on its catalog card.
 */
function vialVariantFor(baseName) {
  const n = (baseName || "").toLowerCase();
  if (n.includes("tirzepatide")) return "tirzepatide";
  return "semaglutide";
}

/** Public browse-only catalog of available medications. NO prices shown —
 * pricing lives on the dedicated /pricing page so it stays in one place.
 * Visitors can see what we offer without signing in, then click "Get started"
 * to begin the qualifying flow. */
export default function BrowseMedicationsPage() {
  const [meds, setMeds] = useState([]);
  const [loading, setLoading] = useState(true);
  const [active, setActive] = useState(null);

  useEffect(() => {
    API.get("/medications")
      .then(({ data }) => setMeds(Array.isArray(data) ? data : []))
      .catch(() => setMeds([]))
      .finally(() => setLoading(false));
  }, []);

  /**
   * Dedupe by **base medication name** (strip "with B12", trim, lowercase).
   * The API already returns one row per medication with strengths nested in
   * `strengths[]`, but we also normalise on the client so a future content
   * edit that introduces a `Semaglutide` row alongside `Semaglutide with B12`
   * won't accidentally show both on the catalog. The catalog should always
   * show one card per active ingredient.
   */
  const uniqueMeds = useMemo(() => {
    const seen = new Map();
    for (const m of meds) {
      const base = (m.name || "").split(/\s+with\s+/i)[0].trim().toLowerCase();
      if (!base || seen.has(base)) continue;
      seen.set(base, m);
    }
    return Array.from(seen.values());
  }, [meds]);

  return (
    <div className="min-h-screen bg-gradient-to-b from-purple-50 to-white" data-testid="browse-medications-page">
      <nav className="max-w-6xl mx-auto px-4 sm:px-6 py-5 flex items-center justify-between">
        <Link to="/" className="inline-flex items-center gap-2" data-testid="browse-meds-home">
          <div className="w-8 h-8 rounded-full bg-purple-600 flex items-center justify-center">
            <Pill className="w-4 h-4 text-white" />
          </div>
          <span className="font-bold text-xl text-slate-900" style={{ fontFamily: "'Outfit', sans-serif" }}>
            GLP<span className="text-purple-600">Flo</span>
          </span>
        </Link>
        <div className="flex items-center gap-2">
          <Link to="/login" className="text-sm font-medium text-slate-600 hover:text-purple-600">Sign in</Link>
          <Link to="/register">
            <Button className="bg-purple-600 hover:bg-purple-700 text-white rounded-full px-4" size="sm" data-testid="browse-meds-get-started">
              Get started
            </Button>
          </Link>
        </div>
      </nav>

      <header className="max-w-3xl mx-auto px-4 sm:px-6 pt-6 pb-10 text-center">
        <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-slate-900" style={{ fontFamily: "'Outfit', sans-serif" }}>
          Our medications
        </h1>
        <p className="mt-3 text-slate-600 max-w-xl mx-auto text-sm sm:text-base">
          Physician-prescribed GLP-1 therapy delivered to your door. All medications are
          compounded by US-licensed 503A pharmacies under physician oversight.
        </p>
        <p className="mt-4 text-xs text-slate-400">
          Tap a medication to learn how it works. Pricing is shown after a free 30-second eligibility quiz so we can match you with the right plan.
        </p>
      </header>

      <section className="max-w-6xl mx-auto px-4 sm:px-6 pb-16">
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <Loader2 className="w-6 h-6 text-purple-500 animate-spin" />
          </div>
        ) : uniqueMeds.length === 0 ? (
          <Card><CardContent className="p-10 text-center text-slate-500">No medications listed yet.</CardContent></Card>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
            {uniqueMeds.map((m) => {
              const baseName = (m.name || "").split(/\s+with\s+/i)[0].trim();
              return (
                <button
                  key={m.id}
                  type="button"
                  onClick={() => setActive(m)}
                  className="group text-left"
                  data-testid={`browse-med-card-${m.id}`}
                  aria-label={`Learn more about ${baseName}`}
                >
                  <Card className="border-slate-200 transition-all duration-500 ease-out hover:shadow-2xl hover:shadow-purple-200/60 hover:-translate-y-1 group-hover:border-purple-300 cursor-pointer h-full overflow-hidden">
                    {/* Illustrated mock vial — purely decorative, NOT a photo of
                        the dispensed product (which varies by pharmacy). */}
                    <div className="relative h-44 bg-gradient-to-b from-purple-50 via-violet-50 to-white flex items-center justify-center pt-3 pb-1">
                      <VialIllustration variant={vialVariantFor(baseName)} />
                      <span className="absolute top-2 right-2 text-[9px] uppercase tracking-wider font-semibold text-purple-400/80 bg-white/70 backdrop-blur rounded-full px-2 py-0.5">
                        Illustration
                      </span>
                    </div>
                    <CardContent className="p-6 pt-5">
                      <div className="flex items-start justify-between mb-3">
                        <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-purple-100 to-violet-100 flex items-center justify-center">
                          <Pill className="w-5 h-5 text-purple-600" />
                        </div>
                        <span className="inline-flex items-center gap-1 text-[11px] text-purple-600 font-semibold opacity-70 group-hover:opacity-100 transition-opacity">
                          <Info className="w-3 h-3" /> Learn more
                        </span>
                      </div>
                      <h3 className="font-bold text-lg text-slate-900" style={{ fontFamily: "'Outfit', sans-serif" }} data-testid={`browse-med-name-${m.id}`}>
                        {baseName}
                      </h3>
                      <p className="text-xs uppercase tracking-wider text-purple-600 font-semibold mt-0.5">
                        {m.type || "GLP-1"}
                      </p>
                      {m.description && (
                        <p className="text-sm text-slate-600 mt-3 leading-relaxed line-clamp-3">{m.description}</p>
                      )}
                    </CardContent>
                  </Card>
                </button>
              );
            })}
          </div>
        )}

        {/* Illustration disclaimer — the marketing mock vial is not a photo of
            the actual dispensed product. Real vials are labelled by the
            licensed compounding pharmacy, not GLPFlo. */}
        {!loading && uniqueMeds.length > 0 && (
          <p className="mt-6 text-center text-[11px] text-slate-400 italic max-w-2xl mx-auto leading-relaxed" data-testid="vial-illustration-disclaimer">
            Vial images shown are stylised illustrations for reference only.
            Your actual medication will arrive in packaging branded by the
            licensed US compounding pharmacy that fills your prescription,
            not GLPFlo.
          </p>
        )}

        <div className="mt-12 rounded-2xl bg-purple-600 text-white p-8 sm:p-10 text-center">
          <h2 className="text-2xl sm:text-3xl font-bold" style={{ fontFamily: "'Outfit', sans-serif" }}>
            Ready to see your match?
          </h2>
          <p className="mt-2 text-purple-100 max-w-md mx-auto text-sm">
            Take our free 30-second quiz to see which medication fits your goals and your budget.
          </p>
          <Link to="/register">
            <Button
              size="lg"
              className="mt-5 bg-white text-purple-700 hover:bg-purple-50 rounded-full px-7"
              data-testid="browse-meds-cta-quiz"
            >
              Start the quiz <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          </Link>
        </div>
      </section>

      <MedicationInfoModal med={active} onClose={() => setActive(null)} />

      <PublicFooter />
    </div>
  );
}
